import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { DollarSign, TrendingUp, Users, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color = "primary" }) => (
  <Card className="bg-card border-border rounded-sm">
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
          color === "primary" ? "bg-primary/20" : "bg-secondary"
        }`}>
          <Icon size={24} className={color === "primary" ? "text-primary" : "text-muted-foreground"} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground font-['Chivo']">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const PaySheet = () => {
  const [payData, setPayData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayData();
  }, []);

  const fetchPayData = async () => {
    try {
      const response = await axios.get(`${API}/admin/pay-sheet`);
      setPayData(response.data);
    } catch (error) {
      console.error("Failed to fetch pay data:", error);
    } finally {
      setLoading(false);
    }
  };

  const totals = payData.reduce(
    (acc, item) => ({
      total_calls: acc.total_calls + item.total_calls,
      qualified_leads: acc.qualified_leads + item.qualified_leads,
      appointments_set: acc.appointments_set + item.appointments_set,
      total_pay: acc.total_pay + item.total_pay,
    }),
    { total_calls: 0, qualified_leads: 0, appointments_set: 0, total_pay: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pay-sheet-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-['Chivo']">Pay Sheet</h1>
        <p className="text-muted-foreground">Caller performance and payment summary</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Callers"
          value={payData.length}
          icon={Users}
          color="secondary"
        />
        <StatCard
          title="Total Calls"
          value={totals.total_calls}
          icon={Phone}
          color="secondary"
        />
        <StatCard
          title="Qualified Leads"
          value={totals.qualified_leads}
          icon={TrendingUp}
          color="primary"
        />
        <StatCard
          title="Total Payout"
          value={`$${totals.total_pay.toFixed(2)}`}
          icon={DollarSign}
          color="primary"
        />
      </div>

      {/* Pay Sheet Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-['Chivo']">Caller Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Caller
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Total Calls
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Qualified
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Appointments
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Base Pay
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Bonus
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Total Pay
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No pay data available</p>
                      <p className="text-sm mt-1">Call logs will populate this sheet</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {payData.map((item) => (
                      <TableRow key={item.caller_id} className="hover:bg-secondary/20">
                        <TableCell className="font-medium">
                          {item.caller_name || "Unknown"}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.total_calls}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <span className="text-[hsl(var(--success))]">
                            {item.qualified_leads}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.appointments_set}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${item.base_pay.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <span className="text-primary">
                            ${item.bonus.toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          ${item.total_pay.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-secondary/30 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right font-mono">
                        {totals.total_calls}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[hsl(var(--success))]">
                        {totals.qualified_leads}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {totals.appointments_set}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ${payData.reduce((acc, i) => acc + i.base_pay, 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-primary">
                        ${payData.reduce((acc, i) => acc + i.bonus, 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ${totals.total_pay.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pay Calculation Info */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-['Chivo'] text-base">Pay Calculation</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-secondary/30 p-4 rounded-sm">
              <p className="font-medium text-foreground">Base Pay</p>
              <p className="text-muted-foreground">$2.00 per call</p>
            </div>
            <div className="bg-secondary/30 p-4 rounded-sm">
              <p className="font-medium text-foreground">Qualified Lead Bonus</p>
              <p className="text-muted-foreground">$10.00 per qualified lead</p>
            </div>
            <div className="bg-secondary/30 p-4 rounded-sm">
              <p className="font-medium text-foreground">Appointment Bonus</p>
              <p className="text-muted-foreground">$25.00 per appointment set</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaySheet;
